<template>
  <div class="integration-health-manager">
    <!-- Banner for persistent display -->
    <integration-health-banner 
      v-if="showBanner && hasVisibleHealthIssues"
    />

    <!-- Toast notifications for real-time alerts -->
    <integration-health-toast
      v-for="toast in activeToasts"
      :key="`toast-${toast.integration.integrationType}-${toast.integration.integrationName}`"
      :integration="toast.integration"
      :show="toast.show"
      :timeout="toast.timeout"
      @view-details="handleViewDetails"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { GET_INTEGRATION_HEALTH } from '@/stores/actions.type';
import { NotificationService, NOTIFICATION_TYPES } from '@/services/notification.service';
import IntegrationHealthBanner from './IntegrationHealthBanner.vue';
import IntegrationHealthToast from './IntegrationHealthToast.vue';

export default {
  name: 'IntegrationHealthManager',
  
  components: {
    IntegrationHealthBanner,
    IntegrationHealthToast,
  },

  props: {
    enableBanner: {
      type: Boolean,
      default: true,
    },
    enableToasts: {
      type: Boolean,
      default: true,
    },
    preferBanner: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      activeToasts: [],
      previousHealthData: [],
      toastQueue: [],
    };
  },

  computed: {
    ...mapGetters([
      'hasVisibleHealthIssues',
      'unhealthyIntegrations',
      'integrationHealthData',
    ]),

    showBanner() {
      return this.enableBanner && this.preferBanner;
    },

    showToasts() {
      return this.enableToasts && (!this.preferBanner || !this.enableBanner);
    },


  },

  watch: {
    integrationHealthData: {
      handler(newHealthData, oldHealthData) {
        if (this.enableToasts) {
          this.checkForNewHealthIssues(newHealthData, oldHealthData || []);
        }
      },
      deep: true,
    },
    '$store.state.advisor.currentUser.integrations': {
      handler() {
        this.$forceUpdate();
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions([
      GET_INTEGRATION_HEALTH,
    ]),

    /**
     * Check for new health issues and trigger toast notifications
     */
    checkForNewHealthIssues(newHealthData, oldHealthData) {
      if (!Array.isArray(newHealthData) || !Array.isArray(oldHealthData)) {
        return;
      }

      const oldHealthMap = new Map();
      oldHealthData.forEach(item => {
        const key = `${item.integrationType}-${item.integrationName}`;
        oldHealthMap.set(key, item.health?.status || 'healthy');
      });

      newHealthData.forEach(item => {
        const key = `${item.integrationType}-${item.integrationName}`;
        const oldStatus = oldHealthMap.get(key) || 'healthy';
        const newStatus = item.health?.status || 'healthy';

        const shouldNotify = this.shouldNotifyUser(oldStatus, newStatus, item.health);

        if (shouldNotify && this.showToasts) {
          this.queueToast(item);
        }
      });

      this.processToastQueue();
    },

    /**
     * Determine if user should be notified about health change
     */
    shouldNotifyUser(oldStatus, newStatus, healthData) {
      // New error
      if (oldStatus !== 'error' && newStatus === 'error') {
        return true;
      }
      
      if (oldStatus === 'healthy' && newStatus === 'warning') {
        return true;
      }

      if (newStatus === 'error' && healthData?.errorCount >= 3) {
        return true;
      }

      return false;
    },

    /**
     * Add toast to queue to prevent spam
     */
    queueToast(integration) {
      const toastId = `${integration.integrationType}-${integration.integrationName}`;

      if (this.toastQueue.some(t => t.id === toastId) || 
          this.activeToasts.some(t => t.id === toastId)) {
        return;
      }

      this.toastQueue.push({
        id: toastId,
        integration,
        timeout: this.getToastTimeout(integration.health?.status),
      });
    },

    /**
     * Process toast queue with delays to prevent overwhelming user
     */
    processToastQueue() {
      if (this.toastQueue.length === 0) return;

      const toast = this.toastQueue.shift();
      
      this.activeToasts.push({
        ...toast,
        show: true,
      });

      setTimeout(() => {
        this.removeToast(toast.id);
      }, toast.timeout + 500);

      if (this.toastQueue.length > 0) {
        setTimeout(() => {
          this.processToastQueue();
        }, 2000);
      }
    },

    /**
     * Remove toast from active list
     */
    removeToast(toastId) {
      const index = this.activeToasts.findIndex(t => t.id === toastId);
      if (index > -1) {
        this.activeToasts.splice(index, 1);
      }
    },

    /**
     * Get appropriate timeout for toast based on severity
     */
    getToastTimeout(status) {
      switch (status) {
        case 'error':
          return 10000;
        case 'warning':
          return 8000;
        default:
          return 6000;
      }
    },



    /**
     * Handle view details request
     */
    handleViewDetails(integration) {
      NotificationService.alert({
        title: `${integration.integrationName} Integration Details`,
        message: `
          <div style="text-align: left;">
            <p><strong>Status:</strong> ${integration.health?.status || 'Unknown'}</p>
            <p><strong>Last Checked:</strong> ${this.formatDate(integration.health?.lastChecked)}</p>
            <p><strong>Last Successful:</strong> ${this.formatDate(integration.health?.lastSuccessful)}</p>
            <p><strong>Error Count:</strong> ${integration.health?.errorCount || 0}</p>
            ${integration.health?.errorMessage ? `<p><strong>Last Error:</strong> ${integration.health.errorMessage}</p>` : ''}
          </div>
        `,
        type: NOTIFICATION_TYPES.INFO,
      });
    },

    /**
     * Format date for display
     */
    formatDate(dateString) {
      if (!dateString) return 'Unknown';
      
      const date = new Date(dateString);
      return date.toLocaleString();
    },
  },

  created() {
    this.previousHealthData = [...(this.integrationHealthData || [])];
  },
};
</script>

<style lang="scss" scoped>
.integration-health-manager {
  position: relative;
  z-index: 1000;
  width: 100vw;
}
</style> 